import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import { ReactNode } from 'react';
import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor, closestCorners } from '@dnd-kit/core';
import { toast } from '@/components/ui/use-toast';
import { useVideoDashboardStore } from '@/store/videoDashboard/store';
import { useFilteredVideos } from '@/store/videoDashboard/filters';
import { VideoCard } from '../videos/VideoCard';
import { useDashboard } from './DashboardContext';

interface DashboardDragDropProps {
  children: ReactNode;
}

export function DashboardDragDrop({ children }: DashboardDragDropProps) {
  useLanguage();
  const { activeId, setActiveId } = useDashboard();
  const moveVideoToFolder = useVideoDashboardStore(state => state.moveVideoToFolder);
  const filteredVideos = useFilteredVideos();
  
  // Configure DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px of movement required before drag starts
      },
    })
  );
  
  const handleDragStart = (event: any) => {
    const { active } = event;
    setActiveId(active.id);
  };
  
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (over && active.data.current?.type === 'video') {
      const videoId = active.data.current.videoId;
      const folderId = over.data.current?.folderId;
      const currentFolderId = active.data.current.currentFolderId;
      
      // Only move if dropping into a different folder
      if (folderId && folderId !== currentFolderId) {
        moveVideoToFolder(videoId, folderId);
        
        // Buscar informações do vídeo para melhor feedback
        const videoTitle = active.data.current?.videoTitle || translateInterfaceText("Vídeo");
        
        // Show success toast com mais detalhes
        toast({
          title: translateInterfaceText("✅ \"{value0}\" foi movido com sucesso!", { value0: (videoTitle) }),
          description: translateInterfaceText("O vídeo foi movido para a pasta selecionada.")
        });
      }
    }
  };
  
  const getDragOverlay = () => {
    if (!activeId) return null;
    
    const videoId = activeId.replace('video-', '');
    const video = filteredVideos.find(v => v.id === videoId);
    
    if (!video) return null;
    
    return (
      <div className="relative max-w-[220px]">
        <VideoCard
          id={video.id}
          title={video.title}
          thumbnailUrl={video.thumbnailUrl}
          viewCount={video.views || video.viewCount || 0}
          isProcessing={video.isProcessing || video.status === 'processing'}
          isNew={video.isNew}
          folderId={video.folderId}
          createdAt={video.createdAt || video.uploadDate}
          className="cursor-grabbing shadow-2xl opacity-90 scale-75 ring-4 ring-[#4169E1] ring-opacity-50"
        />
        
        {/* Badge de "Arrastando" */}
        <div className="absolute -top-2 -right-2 bg-[#4169E1] text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg">
          {translateInterfaceText("Arrastando...")}</div>
      </div>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {children}
      <DragOverlay>
        {getDragOverlay()}
      </DragOverlay>
    </DndContext>
  );
}
